import React, { useCallback, useEffect, useRef, useState } from "react";
import { Input, Modal, Spin, message } from "antd";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Icon } from "../../../components/Component";
import { machineryApis } from "../../../api/MachineryNorms";
import { addPendingJob } from "../../../store/slices/misc/pendingJobsSlice";
import {
  setHighlight,
  setExportStatus,
} from "../../../store/slices/misc/highlightSlice";
import { DEFAULT_MACHINERY_HEAD } from "./machineryHeads";

// Focused single-job async export flow (mirrors ManpowerNormsExportModal).
// Requesting an export enqueues a build job in PMS (a BullMQ worker builds the
// xlsx in the upload-template format and stores it in DMS). The modal polls for
// a short GRACE WINDOW:
//   quick job  → show the Download link right here ('ready')
//   slow/queued → hand the job off to BackgroundJobWatcher ('background'): the
//                 user can close, and a toast + header badge fire on completion.
// The full request history still lives on the Exports page / History drawer.

const EXPORTS_HISTORY_PATH = "/pms/excelexport-requested-list";
const GRACE_INTERVAL_MS = 2500;
const GRACE_ATTEMPTS = 4; // ~10s grace before background hand-off

type ExportMode = "idle" | "generating" | "ready" | "background" | "failed";

const buildExportJob = (reportId: number) => ({
  jobId: `export-${reportId}`,
  kind: "export" as const,
  reportId,
  label: "Machinery Norms Master export",
  historyPath: EXPORTS_HISTORY_PATH,
  // Tell the BackgroundJobWatcher to poll the MACHINERY export list.
  listKind: "machinery" as const,
  createdAt: Date.now(),
});

const MachineryNormsExportModal = ({
  open,
  onClose,
  onViewExports,
  projectId = 0,
  category = DEFAULT_MACHINERY_HEAD,
}: {
  open: boolean;
  onClose: () => void;
  /** Opens the combined Machinery History drawer on its Exports tab. Falls back
   *  to navigating to the global exports page when not provided. */
  onViewExports?: () => void;
  /** Scope the export to this projectId (0 = org master) + category head. */
  projectId?: number;
  category?: string;
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [mode, setMode] = useState<ExportMode>("idle");
  const [comment, setComment] = useState("");
  const [record, setRecord] = useState<any>(null); // the Processed export row
  const [errorText, setErrorText] = useState("");

  // Generation token — bumped on close / re-generate / background hand-off so a
  // stale in-flight grace loop detects it was superseded and stops.
  const pollGen = useRef(0);
  // Mirrors for use inside handleClose without re-arming callbacks.
  const modeRef = useRef<ExportMode>("idle");
  const reportIdRef = useRef<number | null>(null);
  modeRef.current = mode;

  const resetState = useCallback(() => {
    pollGen.current++;
    reportIdRef.current = null;
    setMode("idle");
    setRecord(null);
    setErrorText("");
    setComment("");
  }, []);

  useEffect(() => {
    if (open) {
      resetState();
    } else {
      pollGen.current++; // stop any running grace loop when the modal closes
    }
    return () => {
      pollGen.current++;
    };
  }, [open, resetState]);

  // Grace-poll the export list for our reportId. Resolves the modal to 'ready'
  // / 'failed' if it finishes quickly, else hands off to the background watcher.
  const gracePoll = useCallback(
    async (reportId: number) => {
      const gen = ++pollGen.current;
      for (let attempt = 0; attempt < GRACE_ATTEMPTS; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, GRACE_INTERVAL_MS));
        if (pollGen.current !== gen) return; // superseded (closed / re-generated)
        try {
          const res = await machineryApis.fetchExportList();
          if (pollGen.current !== gen) return;
          const rows: any[] = res?.data?.records || [];
          const row = rows.find((r) => Number(r?.reportId) === reportId);
          if (row?.status === "Processed" && row?.filePath) {
            setRecord(row);
            setMode("ready");
            dispatch(setExportStatus("ready")); // green mark on History
            return;
          }
          if (row?.status === "Failed") {
            setErrorText(row?.error || "The export could not be generated.");
            setMode("failed");
            dispatch(setExportStatus("failed")); // red mark on History
            return;
          }
        } catch (error) {
          // transient — keep trying within the grace window
          console.error("machinery export grace poll failed:", error);
        }
      }
      // Grace exhausted, still pending → hand off to the background watcher.
      if (pollGen.current !== gen) return;
      dispatch(addPendingJob(buildExportJob(reportId)));
      dispatch(setHighlight(true));
      setMode("background");
      pollGen.current++; // stop this loop; the watcher now owns the job
    },
    [dispatch]
  );

  const handleGenerate = async () => {
    if (mode === "generating") return;
    setErrorText("");
    setRecord(null);
    setMode("generating");
    try {
      const res = await machineryApis.requestExport(
        comment.trim() ? comment.trim() : undefined,
        projectId,
        category
      );
      const reportId = Number(res?.data?.reportId);
      if (!res?.status || !reportId) {
        throw new Error(res?.message || "Could not request the export.");
      }
      reportIdRef.current = reportId;
      dispatch(setExportStatus("pending")); // amber "generating" mark on History
      message.success("Export requested. Generating…");
      gracePoll(reportId);
    } catch (error: any) {
      console.error("Error requesting machinery export:", error);
      setErrorText(error?.message || "Could not request the export.");
      setMode("failed");
      dispatch(setExportStatus("failed"));
    }
  };

  const goToExports = () => {
    if (onViewExports) {
      // hand off to the combined Machinery History drawer (Exports tab)
      handleClose();
      onViewExports();
      return;
    }
    handleClose();
    navigate(EXPORTS_HISTORY_PATH);
  };

  const handleClose = () => {
    // A job still generating when the user closes must not be lost — register it
    // with the background watcher (dedupes with any background hand-off).
    if (modeRef.current === "generating" && reportIdRef.current) {
      dispatch(addPendingJob(buildExportJob(reportIdRef.current)));
      dispatch(setHighlight(true));
    }
    pollGen.current++;
    onClose();
  };

  return (
    <Modal
      title="Export Machinery Norms Master"
      open={open}
      onCancel={handleClose}
      footer={null}
      width={560}
      destroyOnClose
    >
      {/* ---------------- idle ---------------- */}
      {mode === "idle" && (
        <>
          <p className="text-soft">
            Generate the full machinery norms master as an Excel file
            (upload-template format). It builds in the background — we'll show a
            download link here if it's quick, otherwise you can close this and
            we'll notify you when it's ready.
          </p>
          <div className="mb-3">
            <label className="form-label">Comment (optional)</label>
            <Input.TextArea
              rows={2}
              value={comment}
              maxLength={500}
              placeholder="Add a note for this export (optional)"
              onChange={(e) => setComment(e.target.value)}
            />
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <Button color="primary" size="sm" onClick={handleGenerate}>
              <Icon name="download-cloud" />
              <span>Generate Export</span>
            </Button>
            <span className="inlineTableCellLink" onClick={goToExports}>
              View all exports →
            </span>
          </div>
        </>
      )}

      {/* ---------------- generating ---------------- */}
      {mode === "generating" && (
        <div className="text-center py-4">
          <Spin />
          <h6 className="mt-3">Generating your export…</h6>
          <p className="text-soft mb-0">
            Building the machinery norms master file. This normally takes a few
            seconds.
          </p>
        </div>
      )}

      {/* ---------------- ready ---------------- */}
      {mode === "ready" && record && (
        <div className="text-center py-4">
          <Icon
            name="check-circle"
            style={{ fontSize: 42, color: "#1ee0ac" }}
          />
          <h6 className="mt-2">Your export is ready</h6>
          <p className="text-soft mb-3">
            The machinery norms master file has been generated.
          </p>
          {/* Plain styled anchor — NOT an antd Button (global CSS forces a blue
              background on themed buttons). */}
          <a
            href={record.filePath}
            target="_blank"
            rel="noreferrer"
            download
            title={record?.fileName || "Download export"}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              color: "#1ee0ac",
              fontWeight: 500,
              fontSize: 16,
            }}
          >
            <Icon name="download" />
            <span>Download</span>
          </a>
          <div className="mt-4">
            <span className="inlineTableCellLink" onClick={goToExports}>
              View all exports →
            </span>
          </div>
        </div>
      )}

      {/* ---------------- background ---------------- */}
      {mode === "background" && (
        <div className="text-center py-4">
          <Icon name="clock" style={{ fontSize: 40, color: "#f4bd0e" }} />
          <h6 className="mt-2">Still generating…</h6>
          <p className="text-soft mb-3">
            This is taking a little longer, so it'll keep building in the
            background. You can close this window — we'll notify you when it's
            ready, and you'll find it under Exports.
          </p>
          <Button color="primary" size="sm" onClick={goToExports}>
            <Icon name="external" />
            <span>View all exports</span>
          </Button>
        </div>
      )}

      {/* ---------------- failed ---------------- */}
      {mode === "failed" && (
        <div className="text-center py-4">
          <Icon
            name="cross-circle"
            style={{ fontSize: 42, color: "#e85347" }}
          />
          <h6 className="mt-2">Export failed</h6>
          <p className="text-soft mb-3">
            {errorText || "The export could not be generated."}
          </p>
          <Button color="primary" size="sm" onClick={() => resetState()}>
            <Icon name="redo" />
            <span>Try again</span>
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default MachineryNormsExportModal;
